/**
 * Sample reports: tick covers → "Download selected (n)" → a short lead form
 * → the PDFs download straight away (staggered, as the design does).
 *
 * Or, when Admin → IP Intel Report → Samples says so (config sample_delivery,
 * October 2026), they are e-mailed instead ("email": the server sends no
 * download link at all) or as well ("both"). The prop only picks the words
 * before the form is sent; what happens after follows the server's answer,
 * so a page that loaded before the admin flipped the switch still ends right.
 *
 * The lead is kept in sessionStorage, so a second request in the same visit
 * goes straight through — it still posts a lead row (with the same details)
 * so the admin list shows every request, not just the first (the server caps
 * how many sample mails one address gets a day).
 * Download links are short-lived server tokens; the PDFs are never linked
 * from the page itself.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { IprModal } from './Modal';
import {
  API_BASE, coverSrc, ipIntelApi, IpIntelSample, LeadDownload, SampleDelivery, leadPageUrl, sampleDeliveryOf, visitorIds,
} from './api';
import { ipTrack, ipClip } from './track';
import { CONTACT_EMAIL, EMAIL_RE, SAMPLE_INDUSTRY } from './content';

const LEAD_KEY = 'ipintel_lead';

// The card shows no line above the title: neither the university nor the
// patent number (owner, 2026-10-06). `org` stays editable in the admin.

/** The university / institute of a sample: its admin-editable `org` up to
 *  the first "·" ("IIT Indore · Patent 585523 (granted)" -> "IIT Indore"). */
const instituteOf = (r: IpIntelSample) => (r.org || '').split('·')[0].trim();

/** The industry tag shown in place of the description. */
const industryOf = (r: IpIntelSample) => (r.industry || SAMPLE_INDUSTRY[r.id] || '').trim();

interface Lead { first_name: string; last_name: string; email: string; organisation: string; designation: string }
const EMPTY_LEAD: Lead = { first_name: '', last_name: '', email: '', organisation: '', designation: '' };

const storedLead = (): Lead | null => {
  try {
    const v = JSON.parse(sessionStorage.getItem(LEAD_KEY) || 'null');
    return v && v.email ? { ...EMPTY_LEAD, ...v } : null;
  } catch { return null; }
};

const leadValid = (l: Lead) =>
  !!(l.first_name.trim() && l.last_name.trim() && EMAIL_RE.test(l.email.trim()) && l.organisation.trim() && l.designation.trim());

const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
/** "Four full IP Intelligence Reports" — the count follows the admin's active samples. */
const introCount = (n: number) =>
  n === 1 ? 'One full IP Intelligence Report'
    : n > 1 ? `${NUMBER_WORDS[n] || n} full IP Intelligence Reports`
    : 'Full IP Intelligence Reports';

const shortTitle = (t: string) => (t.length > 32 ? `${t.slice(0, 30).trimEnd()}…` : t);

const absolute = (url: string) => (/^https?:\/\//i.test(url) ? url : `${API_BASE()}${url}`);

/** Save one file. Fetch-as-blob keeps our filename across origins (the
 *  `download` attribute is ignored cross-origin); a plain link is the
 *  fallback, and the server's Content-Disposition still makes it a download. */
async function saveFile(d: LeadDownload): Promise<void> {
  const href = absolute(d.url);
  try {
    const res = await fetch(href, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj; a.download = d.filename || 'UniversityTech-Sample-Report.pdf'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 30000);
  } catch {
    const a = document.createElement('a');
    a.href = href; a.download = d.filename || ''; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  }
}

interface Props {
  /** The server's list (admin-editable titles, covers, filenames); the
   *  content.ts fallback only when /config could not be loaded. */
  samples: IpIntelSample[];
  /** /config still loading: placeholder cards, so stale default titles and
   *  covers never flash before the admin's versions arrive. */
  loading?: boolean;
  /** /config's sample_delivery. Missing (an older server, /config failed)
   *  reads as 'download'. */
  sampleDelivery?: SampleDelivery;
  /** Prefills the form's organisation (the IRCC page: "IIT Bombay"); a
   *  visitor's own earlier answer in this tab wins. */
  defaultOrganisation?: string;
  /** Which page the section is on (e.g. 'ircc'): sent with its events and
   *  stored on the lead. */
  surface?: string;
  /** Show the university / institute above each title (the IRCC page). */
  showInstitute?: boolean;
}

/** A cover that fails to load (an upload gone missing) falls back to the
 *  static default once, then stays as it is. */
const Cover: React.FC<{ sample: IpIntelSample; onClick: () => void }> = ({ sample, onClick }) => {
  const [src, setSrc] = useState(() => coverSrc(sample));
  const fallback = `/ip-intel/covers/${sample.id}.png`;
  React.useEffect(() => { setSrc(coverSrc(sample)); }, [sample.id, sample.cover_url]);
  return (
    <img
      src={src}
      alt={`${sample.title} — sample report cover`}
      loading="lazy"
      onClick={onClick}
      onError={() => {
        if (src === fallback) return;
        ipTrack('ipintel_sample_cover_error', { report: sample.id });
        setSrc(fallback);
      }}
      style={{ cursor: 'pointer' }}
    />
  );
};

export const SamplesSection: React.FC<Props> = ({ samples, loading, sampleDelivery, defaultOrganisation, surface, showInstitute }) => {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [showForm, setShowForm] = useState(false);
  const [lead, setLead] = useState<Lead>(() => storedLead() || { ...EMPTY_LEAD, organisation: defaultOrganisation || '' });
  const [hp, setHp] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'downloading' | 'done' | 'emailed'>('idle');
  const [error, setError] = useState('');
  const [links, setLinks] = useState<LeadDownload[]>([]);
  /** Where the mail went (e-mail and both modes) and how many reports it carried. */
  const [sent, setSent] = useState<{ email: string; count: number } | null>(null);

  const mode = sampleDeliveryOf(sampleDelivery);
  const emailOnly = mode === 'email';
  const track = (action: string, props?: Record<string, unknown>) =>
    ipTrack(action, surface ? { surface, ...(props || {}) } : props);

  // The organisation prefill can arrive after the first render (the page's
  // config); it never overwrites what the visitor typed.
  useEffect(() => {
    if (defaultOrganisation) setLead(prev => (prev.organisation ? prev : { ...prev, organisation: defaultOrganisation }));
  }, [defaultOrganisation]);

  const chosen = useMemo(() => samples.filter(s => selected[s.id]), [samples, selected]);
  const count = chosen.length;
  const downloadLabel = emailOnly
    ? (count > 0 ? `Email me the selected reports (${count})` : 'Email me the selected reports')
    : (count > 0 ? `Download selected (${count})` : 'Download selected reports');

  const toggle = (id: string, via: 'cover' | 'checkbox') => {
    const checked = !selected[id];
    track('ipintel_sample_toggle', { report: id, checked, via, index: samples.findIndex(s => s.id === id) + 1 });
    setSelected(prev => ({ ...prev, [id]: checked }));
    if (state === 'done' || state === 'emailed') setState('idle');
  };

  const run = async (l: Lead, reused: boolean) => {
    if (state === 'submitting' || state === 'downloading') return;
    setState('submitting');
    setError('');
    setSent(null);
    track('ipintel_lead_submit', { count, reused, mode });
    const res = await ipIntelApi.submitLead({
      first_name: l.first_name.trim(), last_name: l.last_name.trim(), email: l.email.trim().toLowerCase(),
      organisation: l.organisation.trim(), designation: l.designation.trim(),
      reports: chosen.map(s => s.id),
      user_id: apiService.getStoredUserId?.() || undefined,
      website: hp,
      surface: surface || undefined,
      page_url: leadPageUrl() || undefined,
      ...visitorIds(),
    });
    const data = res.ok ? res.data : null;
    const reports = chosen.map(s => s.id).join(',');
    // E-mail only: the server sent the mail and (by design) no download link.
    // Checked before the downloads test below, which would call it a failure.
    if (data && !data.downloads?.length && data.emailed) {
      try { sessionStorage.setItem(LEAD_KEY, JSON.stringify(l)); } catch { /* ignore */ }
      setShowForm(false);
      setSent({ email: data.email || l.email.trim().toLowerCase(), count });
      track('ipintel_lead_emailed', { count, reused, reports, mode: data.mode || 'email' });
      setState('emailed');
      return;
    }
    if (!data || !data.downloads?.length) {
      const msg = !res.ok ? res.error
        : data?.error || (data?.mode === 'email'
          ? "We couldn't e-mail the reports just now. Please try again in a few minutes."
          : 'The reports could not be prepared. Please try again.');
      track('ipintel_lead_error', { error: ipClip(msg), count, mode: data?.mode || mode });
      setState('idle');
      setError(msg);
      if (reused) setShowForm(true);   // let them see and fix what was sent
      return;
    }
    try { sessionStorage.setItem(LEAD_KEY, JSON.stringify(l)); } catch { /* ignore */ }
    setShowForm(false);
    setState('downloading');
    const dls = data.downloads.map(d => ({
      ...d,
      // The admin-set download name, when the server's link does not carry one.
      filename: d.filename || samples.find(s => s.id === d.id)?.filename || d.filename,
    }));
    track('ipintel_lead_success', { count: dls.length, reused, reports: dls.map(d => d.id).join(',') });
    if (data.emailed) {
      setSent({ email: data.email || l.email.trim().toLowerCase(), count });
      track('ipintel_lead_emailed', { count, reused, reports, mode: data.mode || 'both' });
    }
    setLinks(dls);
    await Promise.all(dls.map((d, i) => new Promise<void>(resolve => {
      setTimeout(() => { void saveFile(d).finally(resolve); }, i * 300);
    })));
    setState('done');
  };

  const onDownloadClick = () => {
    if (!count) return;
    track('ipintel_click_download', { count, reports: chosen.map(s => s.id).join(','), mode });
    setError('');
    const known = storedLead();
    if (known && leadValid(known)) { void run(known, true); return; }
    track('ipintel_lead_form_open', { count });
    setShowForm(true);
  };

  /** "Wrong address? Send again": the form, filled with what was sent. */
  const sendAgain = () => {
    track('ipintel_lead_send_again', { count });
    const known = storedLead();
    if (known) setLead(known);
    setState('idle');
    setError('');
    setShowForm(true);
  };

  const setField = (k: keyof Lead) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setLead(prev => ({ ...prev, [k]: e.target.value }));

  const busy = state === 'submitting' || state === 'downloading';
  const reportsPhrase = count === 1 ? 'selected report' : `${count} selected reports`;
  const sentPhrase = sent ? (sent.count === 1 ? 'the report' : `${sent.count} reports`) : '';

  const input = (k: keyof Lead, id: string, label: string, placeholder: string, type = 'text', auto?: string) => (
    <div>
      <label className="ipr-label" htmlFor={id}>{label}</label>
      <input id={id} type={type} className="ipr-input" placeholder={placeholder} value={lead[k]}
        onChange={setField(k)} autoComplete={auto} disabled={busy} />
    </div>
  );

  return (
    <section id="samples" data-ipintel-section="samples" className="ipr-section" style={{ background: 'var(--ipr-gray-50)' }}>
      <div className="ipr-wrap">
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 48px' }}>
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 12 }}>Sample reports</div>
          <h2 className="ipr-h2" style={{ margin: '0 0 12px' }}>See a report before you order</h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0 }}>
            {introCount(loading ? 0 : samples.length)} delivered for real technologies on Cosmos. Tick as many titles as you like, {emailOnly ? "and we'll e-mail them to you." : 'then download them below.'}
          </p>
        </div>

        {loading ? (
          <div className="ipr-samples-grid" aria-busy="true" aria-label="Loading sample reports">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="ipr-card ipr-sample-card">
                <span className="ipr-skel ipr-sample-skel" />
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <span className="ipr-skel" style={{ width: '50%', height: 10 }} />
                  <span className="ipr-skel" style={{ width: '90%', height: 16 }} />
                  <span className="ipr-skel" style={{ width: '75%', height: 12 }} />
                </div>
              </div>
            ))}
          </div>
        ) : !samples.length ? (
          <p style={{ textAlign: 'center', fontSize: 15, color: 'var(--ipr-text)', margin: '0 auto 32px', maxWidth: 520, lineHeight: 1.6 }}>
            The sample reports are being refreshed. For a copy in the meantime, write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('IP Intelligence Report — sample report')}`}
              onClick={() => track('ipintel_click_contact', { position: 'samples_empty' })}
              style={{ color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline' }}>{CONTACT_EMAIL}</a>.
          </p>
        ) : (
        <div className="ipr-samples-grid">
          {samples.map(r => {
            const checked = !!selected[r.id];
            return (
              <div key={r.id} className={`ipr-card ipr-sample-card${checked ? ' is-checked' : ''}`}>
                <Cover sample={r} onClick={() => toggle(r.id, 'cover')} />
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10, flexGrow: 1 }}>
                  {showInstitute && instituteOf(r) && (
                    <div style={{
                      fontFamily: 'var(--ipr-font-ui)', fontSize: 12, fontWeight: 600, lineHeight: 1.3,
                      letterSpacing: '0.02em', color: 'var(--ipr-gray-300)', marginBottom: -4,
                    }}>{instituteOf(r)}</div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(r.id, 'checkbox')}
                      aria-label={`Select ${r.title}`}
                      style={{ marginTop: 4, width: 18, height: 18, accentColor: '#886A44', flexShrink: 0, cursor: 'pointer' }}
                    />
                    <span title={r.title} style={{
                      fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 16, lineHeight: 1.35, color: 'var(--ipr-heading)',
                      // At most two lines; the rest of a long title becomes "…" (the full title is the tooltip).
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{r.title}</span>
                  </label>
                  <div style={{ flexGrow: 1 }}>
                    {industryOf(r) && (
                      <span style={{
                        display: 'inline-block', fontFamily: 'var(--ipr-font-ui)', fontSize: 12, fontWeight: 600, lineHeight: 1.3,
                        color: 'var(--ipr-primary-700)', background: 'var(--ipr-primary-100)', borderRadius: 999, padding: '4px 10px',
                      }}>{industryOf(r)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {samples.length > 0 && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <button type="button" className="ipr-btn-primary" onClick={onDownloadClick} disabled={count === 0 || busy}>
            {busy && <span className="ipr-spin" aria-hidden="true" />}
            {state === 'submitting' ? (emailOnly ? 'Sending your reports…' : 'Preparing your reports…')
              : state === 'downloading' ? 'Downloading…' : downloadLabel}
          </button>
          {state === 'emailed' && sent ? (
            <div className="ipr-alert ipr-alert-success" role="status" style={{ maxWidth: 480, textAlign: 'center', lineHeight: 1.6 }}>
              <strong>Sent!</strong> We've e-mailed {sentPhrase} to <strong>{sent.email}</strong>. If you don't see{' '}
              {sent.count === 1 ? 'it' : 'them'} in a few minutes, check your spam folder.{' '}
              <button type="button" onClick={sendAgain}
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                Wrong address? Send again
              </button>
            </div>
          ) : state === 'done' ? (
            <div role="status" style={{ fontSize: 13, color: 'var(--ipr-gray-300)', textAlign: 'center', lineHeight: 1.6 }}>
              Your download has started. Didn't get a file?{' '}
              {links.map((l, i) => (
                <React.Fragment key={l.id}>
                  {i > 0 && ' · '}
                  <a href={absolute(l.url)} download={l.filename} style={{ color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline' }}
                    onClick={() => track('ipintel_click_download', { count: 1, retry: true, report: l.id })}>
                    {shortTitle(l.title || samples.find(s => s.id === l.id)?.title || l.filename || l.id)}
                  </a>
                </React.Fragment>
              ))}
              {sent && <><br />We've also e-mailed {sentPhrase} to <strong>{sent.email}</strong>.</>}
            </div>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--ipr-muted)' }}>
              {emailOnly ? "Select one or more reports above and we'll e-mail them to you." : 'Select one or more reports above, then download.'}
            </span>
          )}
          {error && !showForm && <div className="ipr-alert ipr-alert-error" role="alert" style={{ maxWidth: 440, textAlign: 'center' }}>{error}</div>}
        </div>
        )}
      </div>

      {showForm && (
        <IprModal labelledBy="ipr-lead-title" onClose={via => { if (busy) return; track('ipintel_lead_form_close', { via, count }); setShowForm(false); }} locked={busy}>
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 10 }}>{emailOnly ? 'Before we send them' : 'Before you download'}</div>
          <div id="ipr-lead-title" style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>A few details first</div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 24px' }}>
            {emailOnly
              ? `Tell us who you are and we'll e-mail the ${reportsPhrase} to you.`
              : mode === 'both'
                ? `Tell us who you are and we'll send the ${reportsPhrase} straight to your desktop, with a copy by e-mail.`
                : `Tell us who you are and we'll send the ${reportsPhrase} straight to your desktop.`}
          </p>
          <form
            noValidate
            onSubmit={e => { e.preventDefault(); if (leadValid(lead)) void run(lead, false); }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="ipr-grid-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {input('first_name', 'ipr-lead-first', 'First name', 'Ada', 'text', 'given-name')}
                {input('last_name', 'ipr-lead-last', 'Last name', 'Lovelace', 'text', 'family-name')}
              </div>
              {input('email', 'ipr-lead-email', 'Email', 'you@company.com', 'email', 'email')}
              {input('organisation', 'ipr-lead-org', 'Organisation', 'Your company or institute', 'text', 'organization')}
              {input('designation', 'ipr-lead-designation', 'Designation', 'e.g. Head of Tech Transfer', 'text', 'organization-title')}
              <div className="ipr-hp" aria-hidden="true">
                <label htmlFor="ipr-lead-website">Website</label>
                <input id="ipr-lead-website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
              </div>
            </div>
            {error && <div className="ipr-alert ipr-alert-error" role="alert" style={{ marginTop: 16 }}>{error}</div>}
            <button type="submit" className="ipr-btn-primary" style={{ width: '100%', marginTop: 24 }} disabled={!leadValid(lead) || busy}>
              {busy && <span className="ipr-spin" aria-hidden="true" />}
              {state === 'submitting' ? (emailOnly ? 'Sending…' : 'Preparing…') : emailOnly ? 'Email me the reports' : 'Download reports'}
            </button>
          </form>
          <div style={{ fontSize: 12, color: 'var(--ipr-muted)', textAlign: 'center', marginTop: 12 }}>
            Used only to send you the reports and relevant follow-ups.
          </div>
        </IprModal>
      )}
    </section>
  );
};
